/**
 * DeliveryStatusPage — the per-content fleet view behind "View delivery
 * status" (delivery-status capability). One aggregate read answers for every
 * scope; rows render ledger/hosted/served states with their provenance, the
 * most actionable first. The per-row "Check now" mounts the identity-pinned
 * badge, which runs the full served-truth check for that single scope
 * immediately — no cycle wait, no other scope touched.
 *
 * Copy system: delivery/availability vocabulary only — never "deploy".
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AdminPage,
  AdminPageHeader,
  AdminPanel,
  AdminStatusMessage,
  adminButton,
  adminField,
  adminFocusable,
  adminTokens as a,
  type AdminStatusTone,
} from '../admin-ui';
import {
  useDeliveryStatus,
  type DeliveryScopeEntry,
  type DeliveryScopeState,
  type DeliveryStatusSnapshot,
} from '../api/deliveryStatusApi';
import { useCourse } from '../api/courseRecordApi';
import { useLessonQuery } from '../api/courseApi';
import { CdnLiveStatus, type CdnStatus } from '../components/CdnLiveStatus';

const PAGE_CHUNK = 50;


const STATE_LABELS: Record<DeliveryScopeState, string> = {
  current: 'Serving latest publish',
  propagating: 'Preparing delivery',
  stale: 'Not serving the latest publish',
  partial: 'Delivery incomplete',
  neverDeployed: 'Not delivered yet',
  failed: 'Delivery failed',
  unknown: 'Delivery unknown',
};

/** Most actionable first; base is pinned above everything separately. */
const STATE_ORDER: Record<DeliveryScopeState, number> = {
  failed: 0,
  stale: 1,
  partial: 2,
  neverDeployed: 3,
  unknown: 4,
  propagating: 5,
  current: 6,
};

const SOURCE_LABELS: Record<DeliveryScopeEntry['source'], string> = {
  ledger: 'recorded',
  origin: 'hosted',
  verified: 'verified',
};

/**
 * Fast-check reconciliation: the badge's served-truth verdict overrides the
 * row's recorded/hosted state so the two can never contradict each other.
 * 'superseded' and 'checking' apply no overlay.
 */
const VERDICT_STATE: Partial<Record<CdnStatus, DeliveryScopeState>> = {
  live: 'current',
  propagating: 'propagating',
  invalidating: 'propagating',
  'ttl-window': 'propagating',
  'lessons-propagating': 'propagating',
  'lesson-missing': 'stale',
  'lesson-stale': 'stale',
  'lessons-unverified': 'unknown',
  'lesson-partial': 'partial',
  'partial-deploy': 'partial',
  'deploy-missing': 'stale',
  'not-deployed': 'neverDeployed',
  unverified: 'unknown',
  unavailable: 'unknown',
};

// Same three groups as before (delivered / in progress / failed), drawn in the
// admin status tones so the label text keeps 4.5:1 contrast.
const stateTone = (state: DeliveryScopeState): AdminStatusTone => {
  if (state === 'current') return 'success';
  if (state === 'propagating' || state === 'unknown') return 'warning';
  return 'error';
};

const toneChip = (tone: AdminStatusTone): CSSProperties => ({
  color: a.status[tone].text,
  backgroundColor: a.status[tone].background,
  borderColor: a.status[tone].border,
});

const styles: Record<string, CSSProperties> = {
  backLink: {
    ...adminFocusable,
    alignSelf: 'flex-start',
    fontSize: a.typography.label.size,
    fontWeight: 600,
    color: a.text.link,
    textDecoration: 'none',
  },
  runBanner: {
    border: `1px solid ${a.border.subtle}`,
    borderRadius: a.radius.control,
    backgroundColor: a.surface.panel,
    padding: '10px 14px',
    fontSize: a.typography.label.size,
  },
  summaryRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  summaryChip: {
    borderRadius: a.radius.pill,
    padding: '3px 10px',
    fontSize: '12.5px',
    fontWeight: 700,
    border: `1px solid ${a.border.subtle}`,
    backgroundColor: a.surface.panel,
    color: a.text.primary,
  },
  progressTrack: {
    height: '6px',
    borderRadius: '3px',
    background: a.surface.subtle,
    overflow: 'hidden',
  },
  progressFill: { height: '100%', background: a.status.success.text },
  controls: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' },
  search: {
    ...adminField.input,
    height: '36px',
    minWidth: '220px',
    maxWidth: '100%',
    fontSize: a.typography.label.size,
  },
  select: {
    ...adminField.select,
    height: '36px',
    fontSize: a.typography.label.size,
  },
  refresh: {
    ...adminButton.secondary,
    minHeight: '36px',
    fontSize: a.typography.label.size,
  },
  rows: { display: 'flex', flexDirection: 'column' },
  row: {
    padding: '12px 0',
    borderTop: `1px solid ${a.border.subtle}`,
  },
  rowFirst: { borderTop: 'none', paddingTop: 0 },
  rowHead: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  slug: { fontWeight: 700, fontSize: a.typography.body.size, color: a.text.primary },
  customized: {
    fontSize: '11px',
    fontWeight: 700,
    color: a.text.secondary,
    backgroundColor: a.surface.subtle,
    borderRadius: a.radius.pill,
    padding: '1px 8px',
  },
  chip: {
    fontSize: '12.5px',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: a.radius.pill,
    border: '1px solid transparent',
  },
  provenance: { fontSize: '11px', color: a.text.secondary },
  meta: { fontSize: a.typography.caption.size, color: a.text.secondary },
  detailText: { fontSize: '12.5px', color: a.text.secondary, marginTop: '6px' },
  errorText: { fontSize: '12.5px', color: a.status.error.text, marginTop: '6px' },
  checkButton: {
    ...adminButton.link,
    marginLeft: 'auto',
  },
  showMore: {
    ...adminButton.secondary,
    display: 'flex',
    margin: '14px auto 0',
    fontSize: a.typography.label.size,
  },
  statusNote: { fontSize: '13.5px', color: a.text.secondary, padding: '24px 0' },
  runError: { color: a.status.error.text, fontWeight: 700 },
};

const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

function RunBanner({ run }: { run: NonNullable<DeliveryStatusSnapshot['run']> }) {
  if (run.state === 'complete' && run.failedScopeCount === 0) return null;
  const counts = `${run.recordedScopeCount} of ${run.intendedScopeCount} account contexts processed`;
  // Run-level failure: the fan-out died before any account context was
  // targeted, so there are no per-context rows to explain it.
  if (run.error) {
    // The recorded message carries its own scope ("the update for Acme…" vs
    // fleet-wide) — the banner frames it neutrally rather than overstating a
    // single-context failure as "anywhere".
    return (
      <div style={styles.runBanner} role="status">
        <span style={styles.runError}>
          The last publish attempt failed before delivery: {run.error}
        </span>
      </div>
    );
  }
  return (
    <div style={styles.runBanner} role="status">
      {run.state === 'running' && (
        <span>Publishing in progress — {counts}. Keep the publishing tab open until it finishes.</span>
      )}
      {run.state === 'stalled' && (
        <span style={styles.runError}>
          The last publish looks interrupted — {counts}. The remaining contexts kept their previous
          version; publish an update to finish delivering everywhere.
        </span>
      )}
      {run.state === 'complete-with-failures' && (
        <span style={styles.runError}>
          The last publish completed with {run.failedScopeCount} failed
          {run.failedScopeCount === 1 ? ' context' : ' contexts'} — see the rows below, then publish
          an update to retry.
        </span>
      )}
      {run.state === 'complete' && run.failedScopeCount > 0 && <span>{counts}</span>}
    </div>
  );
}

function ScopeRow({
  entry,
  packageId,
  basePublishedAt,
  isFirst,
}: {
  entry: DeliveryScopeEntry;
  packageId: string;
  basePublishedAt: string | null;
  isFirst: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  // The fast check's served-truth verdict reconciles the row: state chip and
  // provenance flip to the verified result the moment the badge settles.
  const [verdict, setVerdict] = useState<DeliveryScopeState | null>(null);
  useEffect(() => {
    setVerdict(null); // a fresh aggregate entry supersedes an old fast check
  }, [entry.state, entry.source, entry.deployId, entry.deployedAt]);
  const shownState = verdict ?? entry.state;
  const shownSource: DeliveryScopeEntry['source'] = verdict ? 'verified' : entry.source;
  const label = entry.base ? 'Standard (Emtrain)' : entry.accountSlug;
  const lessonsNote = entry.lessons && (entry.lessons.stale > 0 || entry.lessons.missing > 0)
    ? [
        entry.lessons.stale > 0 ? `${entry.lessons.stale} lesson${entry.lessons.stale === 1 ? '' : 's'} behind the latest publish` : null,
        entry.lessons.missing > 0 ? `${entry.lessons.missing} lesson${entry.lessons.missing === 1 ? '' : 's'} unavailable` : null,
      ].filter(Boolean).join(' · ')
    : null;

  return (
    <div style={{ ...styles.row, ...(isFirst ? styles.rowFirst : {}) }}>
      <div style={styles.rowHead}>
        <span style={styles.slug}>{label}</span>
        {entry.hasCustomizations && <span style={styles.customized}>customized</span>}
        <span style={{ ...styles.chip, ...toneChip(stateTone(shownState)) }}>
          {/* Served-verified WORDING is reserved for verified results (spec:
              provenances are not conflated) — records/hosted evidence claims
              delivery, never serving. */}
          {shownState === 'current' && shownSource !== 'verified'
            ? 'Delivered — latest publish'
            : STATE_LABELS[shownState]}
          {shownState === 'propagating' && entry.expectedLiveAt
            ? ` — expected by ${formatTime(entry.expectedLiveAt)}`
            : ''}
        </span>
        <span style={styles.provenance}>({SOURCE_LABELS[shownSource]})</span>
        {entry.deployedAt && <span style={styles.meta}>delivered {formatTime(entry.deployedAt)}</span>}
        {entry.deployId && <span style={styles.meta}>#{entry.deployId.slice(0, 8)}</span>}
        <button type="button" style={styles.checkButton} onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Hide check' : 'Check now'}
        </button>
      </div>
      {lessonsNote && <div style={styles.detailText}>{lessonsNote}</div>}
      {entry.ledger?.error && <div style={styles.errorText}>{entry.ledger.error}</div>}
      {entry.ledger?.warning && !entry.ledger.error && (
        <div style={styles.detailText}>{entry.ledger.warning}</div>
      )}
      {expanded && (
        <CdnLiveStatus
          packageId={packageId}
          accountSlug={entry.base ? undefined : entry.accountSlug}
          expectedDeployId={entry.deployId}
          // Each scope's own delivery expectation (max of base and account
          // publish) feeds the publish-gap heuristic, so an old-but-edge-live
          // package can never show green under a stale row.
          publishedAt={entry.expectedPublishedAt ?? (entry.base ? basePublishedAt : null)}
          pollToken={entry.deployId ?? entry.deployedAt ?? null}
          // Hosted truth over identity pinning: a mismatching (newer) deploy
          // is evaluated, not reported as terminal "superseded" — the row
          // must reconcile to what is actually served.
          supersededBehavior="evaluate"
          onStatusChange={(status) => {
            const mapped = VERDICT_STATE[status];
            if (mapped) setVerdict(mapped);
          }}
        />
      )}
    </div>
  );
}

export function DeliveryStatusPage() {
  const params = useParams<{ courseId?: string; lessonId?: string }>();
  const contentKind = params.courseId ? 'course' : 'lesson';
  const packageId = params.courseId ?? params.lessonId ?? '';
  const editorPath = contentKind === 'course' ? `/courses/${packageId}` : `/lessons/${packageId}`;

  const status = useDeliveryStatus(contentKind, packageId);
  const courseQuery = useCourse(packageId, { enabled: contentKind === 'course' });
  const lessonQuery = useLessonQuery(packageId, { enabled: contentKind === 'lesson' });
  const title = contentKind === 'course'
    ? courseQuery.data?.title
    : lessonQuery.data?.lessonMeta.title;

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<'all' | DeliveryScopeState>('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_CHUNK);

  const rows = useMemo(() => {
    const scopes = status.data?.scopes ?? [];
    const needle = search.trim().toLowerCase();
    const filtered = scopes.filter((entry) =>
      (stateFilter === 'all' || entry.state === stateFilter)
      && (needle === '' || entry.accountSlug.toLowerCase().includes(needle)
        || (entry.base && 'standard (emtrain)'.includes(needle))));
    return [...filtered].sort((a, b) => {
      if (a.base !== b.base) return a.base ? -1 : 1;
      if (STATE_ORDER[a.state] !== STATE_ORDER[b.state]) return STATE_ORDER[a.state] - STATE_ORDER[b.state];
      return a.accountSlug.localeCompare(b.accountSlug);
    });
  }, [search, stateFilter, status.data]);

  const summary = status.data?.summary;
  const settledShare = summary && summary.total > 0 ? summary.current / summary.total : 0;

  return (
    <AdminPage maxWidth="960px">
      <Link to={editorPath} style={styles.backLink}>← Back to editor</Link>
      <AdminPageHeader
        headingLevel="h1"
        title="Delivery status"
        description={(
          <>
            {title ?? '…'} · last published {formatTime(status.data?.publish.lastPublishedAt)}
            {status.data && ` · checked ${formatTime(status.data.checkedAt)}`}
          </>
        )}
      />

      {status.isLoading && (
        <AdminPanel><div style={{ ...styles.statusNote, padding: 0 }}>Loading delivery status…</div></AdminPanel>
      )}
      {status.isError && (
        <AdminStatusMessage tone="error" role="alert">
          The delivery status check is temporarily unavailable.{' '}
          <button type="button" style={{ ...styles.checkButton, marginLeft: 0 }} onClick={() => void status.refetch()}>
            Try again
          </button>
        </AdminStatusMessage>
      )}

      {status.data && summary && (
        <>
          {status.data.run && <RunBanner run={status.data.run} />}
          <AdminPanel style={{ display: 'flex', flexDirection: 'column', gap: a.spacing.md }}>
          <div style={styles.summaryRow}>
            <span style={{ ...styles.summaryChip, ...toneChip('success') }}>
              Latest publish delivered {summary.current}/{summary.total}
            </span>
            {summary.propagating > 0 && (
              <span style={{ ...styles.summaryChip, ...toneChip('warning') }}>
                Preparing {summary.propagating}
              </span>
            )}
            {summary.failed > 0 && (
              <span style={{ ...styles.summaryChip, ...toneChip('error') }}>
                Failed {summary.failed}
              </span>
            )}
            {summary.stale > 0 && (
              <span style={{ ...styles.summaryChip, ...toneChip('error') }}>
                Behind latest {summary.stale}
              </span>
            )}
            {summary.partial > 0 && (
              <span style={{ ...styles.summaryChip, ...toneChip('error') }}>
                Incomplete {summary.partial}
              </span>
            )}
            {summary.neverDeployed > 0 && (
              <span style={styles.summaryChip}>Not delivered {summary.neverDeployed}</span>
            )}
            {summary.unknown > 0 && (
              <span style={styles.summaryChip}>Unknown {summary.unknown}</span>
            )}
          </div>
          <div style={styles.progressTrack} aria-hidden="true">
            <div style={{ ...styles.progressFill, width: `${Math.round(settledShare * 100)}%` }} />
          </div>

          <div style={styles.controls}>
            <input
              type="search"
              placeholder="Find an account context…"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setVisibleCount(PAGE_CHUNK); }}
              style={styles.search}
              aria-label="Find an account context"
            />
            <select
              value={stateFilter}
              onChange={(event) => {
                setStateFilter(event.target.value as 'all' | DeliveryScopeState);
                setVisibleCount(PAGE_CHUNK);
              }}
              style={styles.select}
              aria-label="Filter by delivery state"
            >
              <option value="all">All states</option>
              {(Object.keys(STATE_LABELS) as DeliveryScopeState[]).map((state) => (
                <option key={state} value={state}>{STATE_LABELS[state]}</option>
              ))}
            </select>
            <button
              type="button"
              style={styles.refresh}
              onClick={() => void status.refetch()}
              disabled={status.isFetching}
            >
              {status.isFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          </AdminPanel>

          <AdminPanel style={styles.rows} aria-label="Account contexts">
          {rows.slice(0, visibleCount).map((entry, index) => (
            <ScopeRow
              key={entry.accountSlug}
              entry={entry}
              packageId={packageId}
              basePublishedAt={status.data.publish.lastPublishedAt}
              isFirst={index === 0}
            />
          ))}
          {rows.length === 0 && (
            <div style={{ ...styles.statusNote, padding: 0 }}>No account contexts match the current filters.</div>
          )}
          {rows.length > visibleCount && (
            <button
              type="button"
              style={styles.showMore}
              onClick={() => setVisibleCount((value) => value + PAGE_CHUNK)}
            >
              Show {Math.min(PAGE_CHUNK, rows.length - visibleCount)} more of {rows.length - visibleCount}
            </button>
          )}
          </AdminPanel>
        </>
      )}
    </AdminPage>
  );
}
